import { Plus, Trash2 } from 'lucide-react';

import { inputClass } from '../../components/Field';
import {
  crossesMidnight,
  defaultWindow,
  maxWindows,
  weekdayLabels,
  weekdayNames,
  type RecordingWindow,
} from './api';

interface Props {
  windows: RecordingWindow[];
  onChange: (windows: RecordingWindow[]) => void;
  readOnly: boolean;
  timeZone: string;
  /** Field errors from the API, keyed "schedule", "schedule.0.days"… */
  errors: Record<string, string>;
}

/** Weekly recording windows: days of the week and a start and end time each. */
export function ScheduleEditor({ windows, onChange, readOnly, timeZone, errors }: Props) {
  const update = (i: number, patch: Partial<RecordingWindow>) => {
    onChange(windows.map((w, j) => (j === i ? { ...w, ...patch } : w)));
  };

  return (
    <fieldset className="space-y-2 rounded-md border border-line p-3">
      <legend className="px-1 text-xs font-medium text-fg">Horários de gravação</legend>
      <p className="text-2xs text-fg-subtle">
        No fuso {timeZone || 'da organização'}. Fora destes horários a câmera não grava.
      </p>
      {errors.schedule && (
        <p role="alert" className="text-xs text-crit">
          {errors.schedule}
        </p>
      )}

      <ul className="space-y-2">
        {windows.map((w, i) => {
          const dayError = errors[`schedule.${String(i)}.days`];
          const timeError =
            errors[`schedule.${String(i)}.start`] ?? errors[`schedule.${String(i)}.end`];
          const n = String(i + 1);
          return (
            <li key={i} className="space-y-1.5 rounded border border-line bg-canvas p-2">
              <div className="flex flex-wrap items-center gap-2">
                <div role="group" aria-label={`Dias da faixa ${n}`} className="flex gap-0.5">
                  {weekdayLabels.map((label, d) => {
                    const on = w.days.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        disabled={readOnly}
                        aria-pressed={on}
                        aria-label={weekdayNames[d]}
                        title={weekdayNames[d]}
                        onClick={() => {
                          update(i, {
                            days: on
                              ? w.days.filter((x) => x !== d)
                              : [...w.days, d].sort((a, b) => a - b),
                          });
                        }}
                        className={`h-7 w-9 rounded text-2xs font-medium transition-colors disabled:cursor-default ${
                          on
                            ? 'bg-accent-soft text-accent ring-1 ring-accent/40'
                            : 'text-fg-muted hover:bg-surface-hover hover:text-fg'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <label className="flex items-center gap-1 text-xs text-fg-muted">
                  das
                  <input
                    type="time"
                    aria-label={`Início da faixa ${n}`}
                    disabled={readOnly}
                    value={w.start}
                    onChange={(e) => {
                      update(i, { start: e.target.value });
                    }}
                    className={`${inputClass} h-7 w-auto py-0 font-mono`}
                  />
                </label>
                <label className="flex items-center gap-1 text-xs text-fg-muted">
                  às
                  <input
                    type="time"
                    aria-label={`Fim da faixa ${n}`}
                    disabled={readOnly}
                    value={w.end}
                    onChange={(e) => {
                      update(i, { end: e.target.value });
                    }}
                    className={`${inputClass} h-7 w-auto py-0 font-mono`}
                  />
                </label>
                {!readOnly && (
                  <button
                    type="button"
                    aria-label={`Remover a faixa ${n}`}
                    title="Remover a faixa"
                    onClick={() => {
                      onChange(windows.filter((_, j) => j !== i));
                    }}
                    className="ml-auto grid size-7 place-items-center rounded text-fg-muted transition-colors hover:bg-crit-soft hover:text-crit"
                  >
                    <Trash2 aria-hidden="true" className="size-3.5" />
                  </button>
                )}
              </div>
              {w.start && w.end && crossesMidnight(w) && w.start !== w.end && (
                <p className="text-2xs text-fg-subtle">
                  {w.end === '00:00' ? 'Até a meia-noite.' : 'Termina no dia seguinte.'}
                </p>
              )}
              {w.days.length === 0 && !dayError && (
                <p className="text-2xs text-warn">Escolha pelo menos um dia.</p>
              )}
              {(dayError ?? timeError) && (
                <p role="alert" className="text-2xs text-crit">
                  {dayError
                    ? 'Escolha os dias.'
                    : 'Confira os horários: início e fim não podem ser iguais.'}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {!readOnly && windows.length < maxWindows && (
        <button
          type="button"
          onClick={() => {
            onChange([...windows, defaultWindow]);
          }}
          className="inline-flex h-7 items-center gap-1.5 rounded px-2 text-xs font-medium text-accent hover:bg-accent-soft"
        >
          <Plus aria-hidden="true" className="size-3.5" />
          Adicionar faixa
        </button>
      )}
    </fieldset>
  );
}
